/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState } from 'react';
import { DataGridPro, GridActionsCellItem } from '@mui/x-data-grid-pro';
import apiAdmin from '../../services/api-admin';
import { MdEdit, MdAttachMoney, MdMoneyOff, MdCheckCircle, MdCancel } from 'react-icons/md';
import { useForm, Controller } from "react-hook-form";
import { ToggleButtonGroup, ToggleButton, TextField, MenuItem } from '@mui/material';
import {DateTime} from 'luxon';
import OutlinedDiv from '../layout/OutlinedDiv';
import { Dropzone } from "dropzone";
import "dropzone/dist/dropzone.css";
import { useOktaAuth } from '@okta/okta-react';
import SpinnerLoader from '../layout/spinner-loader';
import InvoicePaymentModal from './reconveyance-paid-invoices';
import ReconveyanceCancelModal from './reconveyance-cancel-orders';
import CustomDataGridToolbar from '../layout/custom-toolbar';

const ReconveyanceOrders = ({ reconveyanceOrders, setReconveyanceOrders, openDoc }) => {
    const { register, handleSubmit, setValue, getValues, reset, formState: { errors }, control } = useForm();
    const { authState } = useOktaAuth();
    const [reconveyanceOrderIdToEdit, setReconveyanceOrderIdToEdit] = useState(null);
    const [reconveyanceOrderToEdit, setReconveyanceOrderToEdit] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [ordersToShow, setOrdersToShow] = useState(false);
    const [orderStatus, setOrderStatus] = useState('Active');
    const [reconDropzone, setReconDropzone] = useState(null);
    const [uccDropzone, setUccDropzone] = useState(null);
    const [uploadedUccFile, setUploadedUccFile] = useState(false);
    const [reconInfoLoadingState, setReconInfoLoadingState] = useState("WAITINGFORFILE");
    const [uccInfoLoadingState, setUccInfoLoadingState] = useState("WAITINGFORFILE");
    const [toggleButtonLabels, setToggleButtonLabels] = useState({ active: "Active", complete: "Complete", cancelled: "Cancelled" });
    const [orderComplete, setOrderComplete] = useState(null);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [modalReconveyanceOrderId, setModalReconveyanceOrderId] = useState(null);
    const [modalReconveyanceOrder, setModalReconveyanceOrder] = useState(null);
    const [counties, setCounties] = useState([]);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelReconveyanceOrderId, setCancelReconveyanceOrderId] = useState(null);

    const columns = [
        { field: 'reconveyanceOrderId', headerName: 'Order Id', export: true, flex: 50 },
        { field: 'lenderName', headerName: 'Lender', flex: 150 },
        { field: 'name', headerName: 'Name', flex: 150 },
        { field: 'orderDate', headerName: 'Order Date', flex: 100, renderCell: function (t) { return t.value.toLocaleDateString("en-US") } },
        { field: 'completeDate', headerName: 'Completed Date', flex: 100, renderCell: function (t) { return t.value ? t.value.toLocaleDateString("en-US") : "";} },
        { field: 'paidAction', headerName: 'Payment', flex: 75, sortable: true,
            valueGetter: (value, row) => {
                if (!row) return 0;

                const { invoiced, paid } = row;

                if (!invoiced) return 0;      
                if (invoiced && !paid) return 1; 
                if (paid) return 2;      

                return 0;
            },

            renderCell: (params) => {
                const { reconveyanceOrderId, invoiced, paid } = params.row;

                if (!invoiced || !parseInt(reconveyanceOrderId)) return null;

                const IconComponent = paid ? MdAttachMoney : MdMoneyOff;

                return (
                    <GridActionsCellItem
                        icon={<IconComponent className="lg-grid-button-icon" style={{ fontSize: 24 }} />}
                        label="Payment"
                        onClick={() => {
                            setModalReconveyanceOrderId(reconveyanceOrderId);
                            setModalReconveyanceOrder(params.row);
                            setShowInvoiceModal(true);
                        }}
                        color="inherit"
                    />
                );
            }
        },
        { field: 'actions', type: 'actions',headerName: 'Edit', flex: 50,
            getActions: (params) => {
                const reconveyanceOrderId = params.row?.reconveyanceOrderId;
                if (!parseInt(reconveyanceOrderId)) return [];
                const actions = [
                    <GridActionsCellItem
                        key="edit"
                        icon={<MdEdit />}
                        label="Edit"
                        onClick={() => setReconveyanceOrderIdToEdit(reconveyanceOrderId)}
                        color="inherit"
                    />
                ];

                if (!params.row.cancelledDate) {
                    actions.push(
                        <GridActionsCellItem
                            key="cancel"
                            icon={<MdCancel />}
                            label="Cancel"
                            onClick={() => {
                                setCancelReconveyanceOrderId(reconveyanceOrderId);
                                setShowCancelModal(true);
                            }}
                            color="inherit"
                        />
                    );
                }
                return actions;
            }
        }
    ];
    
    useEffect(() => {
        const getOrders = async () => {
            setLoadingOrders(true);
            apiAdmin.get(`/get-reconveyance-orders`)
                .then((response) => {
                    setToggleButtonLabels({
                        active: "Active (" + response.data.filter(r => r.completeDate == null && r.cancelledDate == null).length + ')',
                        complete: "Complete (" + response.data.filter(r => r.completeDate != null && r.cancelledDate == null).length + ')',
                        cancelled: "Cancelled (" + response.data.filter(r => r.cancelledDate != null).length + ')',
                    });

                    setReconveyanceOrders(response.data.map(r => ({
                        reconveyanceOrderId: r.reconveyanceOrderId,
                        lenderName: r.lender.name,
                        name: r.preferredCustomer.firstName + " " + r.preferredCustomer.lastName,
                        orderDate: new Date(r.orderDate),
                        completeDate: r.completeDate ? new Date(r.completeDate) : null,
                        invoiced: r.invoiced,
                        paid: r.paid,
                        paidDate: r.paidDate ? DateTime.fromISO(r.paidDate) : null,
                        transactionDetail: r.transactionDetail,
                        cancelledDate: r.cancelledDate ? new Date(r.cancelledDate) : null,
                        cancellationReason: r.cancellationReason || "",
                    })));

                    setLoadingOrders(false);
                    setSubmitting(false);
                });
        };

        getOrders();
    }, [submitted, setReconveyanceOrders, setModalReconveyanceOrder]);

    useEffect(() => {
        if (orderStatus === 'Active') {
            setOrdersToShow(reconveyanceOrders.filter(c => c.completeDate === null && c.cancelledDate === null));
        } else if (orderStatus === 'Complete') {
            setOrdersToShow(reconveyanceOrders.filter(c => c.completeDate !== null && c.cancelledDate === null));
        } else {
            setOrdersToShow(reconveyanceOrders.filter(c => c.cancelledDate !== null));
        }
    }, [reconveyanceOrders, orderStatus]);

    useEffect(() => {
        if (!reconveyanceOrderIdToEdit || reconveyanceOrderIdToEdit === -1) return;

        const getReconveyanceOrder = async () => {
            const response = await apiAdmin.get(`/get-reconveyance-order?reconveyanceOrderId=${reconveyanceOrderIdToEdit}`);
            const order = response.data;

            setReconveyanceOrderToEdit(order);
            setOrderComplete(order.completeDate !== null);
            setValue("sendInvoice", order.lender.invoiceRequired);
            const hasAppointmentFee = order.reconveyanceDocuments?.some(doc => doc.documentType === "Appointment");
            setValue("appointmentFee", hasAppointmentFee);

            const getCounties = async () => {
                apiAdmin.get(`/get-recon-counties?state=${order.stateAbbrev}`)
                    .then((response) => {
                        const countyOptions = response.data;
                        setCounties(countyOptions);
                        const matchedCounty = countyOptions.find(c => c.countyName?.trim().toLowerCase() === order.county?.trim().toLowerCase() );
                        setValue("county", matchedCounty ? matchedCounty.countyName : "");
                    });
            };

            getCounties();

            order.reconveyanceDocuments?.forEach((doc) => {
                switch (doc.documentType) {
                    case "Reconveyance":
                        setValue("reconInstrumentNumber", doc.instrumentNumber || "");
                        setValue("reconRecordedDate", DateTime.fromISO(doc.recordedDate).toFormat("yyyy-MM-dd"));
                        setReconInfoLoadingState("EXISTINGFILEINFOLOADED");
                        break;
                    case "UCC-Unrecorded":
                        setUploadedUccFile(true);
                        break;
                    case "UCC-Recorded":
                        setUploadedUccFile(true);
                        setValue("uccInstrumentNumber", doc.instrumentNumber || "");
                        setValue("uccRecordedDate", DateTime.fromISO(doc.recordedDate).toFormat("yyyy-MM-dd"));
                        setValue("uccRecordingFee", doc.fee ? `$${doc.fee.toFixed(2)}` : "");
                        setUccInfoLoadingState("EXISTINGFILEINFOLOADED");
                        break;
                    default:
                        break;
                }
            });

            setSubmitted(false);
        };

        getReconveyanceOrder();
    }, [reconveyanceOrderIdToEdit, setValue]);

    useEffect(() => {
        if (!reconveyanceOrderToEdit || reconveyanceOrderIdToEdit === null || orderComplete === null) return;
        let tempId = getValues("tempId");
        if (!tempId || tempId === "-1") {
            tempId = crypto.randomUUID();
            setValue("tempId", tempId);
        }
        
        const dtz = new Dropzone("div#dtDropzone", {
            url: `${process.env.REACT_APP_API_ADMIN_BASE_URL}/releaseone/processdoc/${tempId}/Reconveyance/${reconveyanceOrderIdToEdit}`,
            acceptedFiles: '.pdf',
            maxFilesize: 15,
            maxFiles: 1,
            addRemoveLinks: true,
            dictDefaultMessage: "Drag the file here, or click to browse for it.",
            headers: { 'Authorization': `Bearer ${authState?.accessToken?.accessToken}` },
            init: function () {
                this.on("processing", function (file) {
                    setReconInfoLoadingState("FILEPROCESSING");
                    this.options.url = `${process.env.REACT_APP_API_ADMIN_BASE_URL}/releaseone/processdoc/${tempId}/Reconveyance/${reconveyanceOrderIdToEdit}`;
                });

                this.on("removedfile", function (file) {
                    setValue("reconInstrumentNumber", null);
                    setValue("reconRecordedDate", null);
                    setValue("reconFilename", null);
                    setReconInfoLoadingState("WAITINGFORFILE");
                });
            },
            success: function (file, response) {
                setValue("tempId", response.tempId);
                setValue("reconInstrumentNumber", response.instrumentNumber);
                setValue("reconRecordedDate", DateTime.fromISO(response.recordedDate).toFormat("yyyy-MM-dd"));
                setValue("reconFilename", response.filename);
                setReconInfoLoadingState("FILEINFOLOADED");
            }
        });

        orderComplete && dtz.disable();

        setReconDropzone(dtz);

        let uccz;
        if (uploadedUccFile) {
             uccz = new Dropzone("div#uccDropzone", {
                 url: `${process.env.REACT_APP_API_TOOLS_BASE_URL}/releaseone/processdoc/${tempId}/UCC-Recorded/${reconveyanceOrderIdToEdit}`,
                acceptedFiles: '.pdf',
                maxFilesize: 15,
                maxFiles: 1,
                addRemoveLinks: true, 
                dictDefaultMessage: "Drag the file here, or click to browse for it.",
                headers: { 'Authorization': `Bearer ${authState?.accessToken?.accessToken}` },
                 init: function () {
                     this.on("processing", function (file) {
                         setUccInfoLoadingState("FILEPROCESSING");
                         this.options.url = `${process.env.REACT_APP_API_ADMIN_BASE_URL}/releaseone/processdoc/${tempId}/UCC-Recorded/${reconveyanceOrderIdToEdit}`;
                     });

                     this.on("removedfile", function (file) {
                         setValue("uccInstrumentNumber", null);
                         setValue("uccRecordedDate", null);
                         setValue("uccRecordingFee", null);
                         setValue("uccFilename", null);
                         setUccInfoLoadingState("WAITINGFORFILE");
                     });
                 },
                 success: function (file, response) {
                     setValue("uccTempId", response.uccTempId);
                     setValue("uccInstrumentNumber", response.instrumentNumber);
                     setValue("uccRecordedDate", DateTime.fromISO(response.recordedDate).toFormat("yyyy-MM-dd"));
                     setValue("uccRecordingFee", response.recordingFee);
                     setValue("uccFilename", response.filename);
                     setUccInfoLoadingState("FILEINFOLOADED");
                 }
             });

            orderComplete && uccz.disable();

            setUccDropzone(uccz);
        }

        return () => {
            dtz.destroy();
            if (uccz) uccz.destroy();
        };

    }, [reconveyanceOrderToEdit, reconveyanceOrderToEdit]);

    function EditToolbar(props) {

        return (
            <CustomDataGridToolbar>
                <div className="col">
                    <ToggleButtonGroup
                        className="float-end"
                        value={orderStatus}
                        exclusive
                        onChange={(e, newStatus) => {
                            if (newStatus !== null) {
                                setOrderStatus(newStatus);
                            }
                        }}
                        aria-label="Order Status Toggle"
                        >
                        <ToggleButton value="Active" aria-label="Active">{toggleButtonLabels.active}</ToggleButton>
                        <ToggleButton value="Complete" aria-label="Complete">{toggleButtonLabels.complete}</ToggleButton>
                        <ToggleButton value="Cancelled" aria-label="Cancelled">{toggleButtonLabels.cancelled}</ToggleButton>
                    </ToggleButtonGroup>
                </div>
            </CustomDataGridToolbar>
        );
    }

    const onSubmit = data => {
        setSubmitting(true); 

        if (!uploadedUccFile) {
            data.uccFilename = null;
            data.uccRecordedDate = null;
            data.uccInstrumentNumber = null;
            data.uccRecordingFee = null;
        }

        const reconObject = {
            ...data,
            reconveyanceOrderId: reconveyanceOrderIdToEdit,
        }

        const saveAndCompleteOrder = async () => {
            await apiAdmin.post(`/complete-reconveyance-order`, reconObject)
                .then((response) => {
                    if (response.data) {
                        setReconveyanceOrderIdToEdit(null);
                        setReconveyanceOrderToEdit(null);
                        setSubmitted(true);
                        setSubmitting(false); 
                        setOrderComplete(null);
                        setUploadedUccFile(false);
                        setReconInfoLoadingState(null);
                        setUccInfoLoadingState(null);
                        reset();
                        reconDropzone?.removeAllFiles();
                        uccDropzone?.removeAllFiles();
                    } else {
                        setError("There was an error saving the documents. Please try again.");
                    }
                });
        };

        saveAndCompleteOrder();
    };

    const renderCurrentDocumentLink = (type, display) => {
        let doc;
        let filename;

        if (type === 'Invoice') {
            const orderId = reconveyanceOrderToEdit?.reconveyanceOrderId;
            if (!orderId) return null;
            filename = `Reconveyance-Invoice-R${orderId}.pdf`;
        } else {
            doc = reconveyanceOrderToEdit?.reconveyanceDocuments?.find(d => d.documentType === type);
            if (!doc) return null;
            filename = doc.filename;
        }

        return (
            <div className="mb-3" key={doc?.id || type}>
                <OutlinedDiv label={display}>
                    <a href="#" onClick={(e) => { e.preventDefault(); openDoc(`/${reconveyanceOrderToEdit.reconveyanceOrderId}/${filename}`); }}>{filename}</a>
                </OutlinedDiv>
            </div>
        );
    };

    return (
        <div>
            <form onSubmit={handleSubmit(onSubmit)} className={reconveyanceOrderToEdit || reconveyanceOrderIdToEdit === -1 ? "" : "d-none"}>
                <div className="border p-3">
                    <h3>Reconveyance Order</h3>
                    <hr />
                    <div className="mb-3 d-flex gap-3">
                        <OutlinedDiv label="Order Id">
                            {reconveyanceOrderToEdit?.reconveyanceOrderId}
                        </OutlinedDiv>
                        <OutlinedDiv label="Order Date">
                            {DateTime.fromISO(reconveyanceOrderToEdit?.orderDate).toFormat('MM-dd-yyyy')}
                        </OutlinedDiv>
                    </div>
                    <div className="mb-3 d-flex gap-3">
                        <OutlinedDiv label="Name">
                            {reconveyanceOrderToEdit?.preferredCustomer.firstName} {reconveyanceOrderToEdit?.preferredCustomer.lastName}
                        </OutlinedDiv>
                        <OutlinedDiv label="Lender Name">
                            {reconveyanceOrderToEdit?.lender.name}
                        </OutlinedDiv>
                    </div>
                    <div className="mb-3 d-flex gap-3">
                        <Controller
                            name="county"
                            control={control}
                            rules={{ required: true }}
                            defaultValue=""
                            render={({ field }) => (
                                <TextField
                                    select
                                    label="County *"
                                    className="w-100"
                                    error={!!errors?.county}
                                    helperText={errors?.county ? "County is required" : ""}
                                    {...field}
                                >
                                    {counties.map((option) => (
                                        <MenuItem key={"c_" + option.countyId} value={option.countyName}>
                                            {option.countyName}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                        />
                        <OutlinedDiv label="State">
                            {reconveyanceOrderToEdit?.stateAbbrev}
                        </OutlinedDiv>
                    </div>
                    <div className="mb-3 d-flex gap-3">
                        <OutlinedDiv label="Client Email">
                            {reconveyanceOrderToEdit?.clientEmail}
                        </OutlinedDiv>
                    </div>
                    {reconveyanceOrderToEdit?.cancelledDate && 
                        <div className="mb-3 d-flex gap-3">
                            <OutlinedDiv label="Cancellation Reason">
                                {reconveyanceOrderToEdit?.cancellationReason}
                            </OutlinedDiv>
                        </div>
                    }
                    {reconveyanceOrderToEdit?.stateAbbrev === "WA" && <div className="mb-3">
                        <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" id="appointmentFee" {...register("appointmentFee")} />
                            <label className="form-check-label" htmlFor="appointmentFee">Add Appointment Fee</label>
                        </div>
                    </div>}
                    {orderComplete && (
                        <div className="mb-3 d-flex gap-3">
                            <div className="col-6">
                                <OutlinedDiv label="Completed Date">
                                    {DateTime.fromISO(reconveyanceOrderToEdit?.completeDate).toFormat('MM-dd-yyyy')}
                                </OutlinedDiv>
                            </div>
                            <div className="col-6 d-flex align-items-center gap-2">
                                {reconveyanceOrderToEdit?.invoiced ? (
                                    <>
                                        <strong className="d-flex align-items-center text-success">
                                            <MdCheckCircle className="me-1" />
                                            Invoice sent
                                        </strong>
                                        {reconveyanceOrderToEdit?.paid && (
                                            <strong className="d-flex align-items-center text-success">
                                                <MdCheckCircle className="me-1" />
                                                Paid
                                            </strong>
                                        )}
                                    </>
                                ) : (
                                    <strong>No invoice sent</strong>
                                )}
                            </div>
                        </div>
                    )}
                    {reconveyanceOrders.find(order => order.reconveyanceOrderId === reconveyanceOrderIdToEdit && order.completeDate !== null) && renderCurrentDocumentLink("Invoice", "Invoice File")}
                    {reconveyanceOrderToEdit?.paid && <div className="mb-3 d-flex gap-3">
                        <OutlinedDiv label="Paid Date">
                            {DateTime.fromISO(reconveyanceOrderToEdit?.paidDate).toFormat('MM-dd-yyyy')}
                        </OutlinedDiv>
                        <OutlinedDiv label="Transaction Details">
                            {reconveyanceOrderToEdit?.transactionDetail}
                        </OutlinedDiv>
                    </div>}
                    {reconveyanceOrderToEdit?.lender.invoiceRequired === false && !orderComplete && (
                        <div className="mb-3">
                            <div className="text-danger">
                                {reconveyanceOrderToEdit?.lender.name} is not set to receive invoices.  Override this for order {reconveyanceOrderToEdit?.reconveyanceOrderId} using the button below.
                            </div>
                        </div>
                    )}
                    {!orderComplete && <div className="mb-3">
                        <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" id="sendInvoice" {...register("sendInvoice")} />
                            <label className="form-check-label" htmlFor="sendInvoice">Send Invoice</label>
                        </div>
                    </div>}
                 
                    {renderCurrentDocumentLink("Deed of Trust", `Deed of Trust File`)}
                    {renderCurrentDocumentLink("Legal Description", `Legal Description for Partial Reconveyance`)}
                    {renderCurrentDocumentLink("Reconveyance", `Current Reconveyance File`)}
                   
                    <div className="mb-3">
                        <OutlinedDiv label="Reconveyance">
                            <div className="row pt-3">
                                <div className="col-12 col-md-9">
                                    <div className="row">
                                        <div className={(reconInfoLoadingState === "FILEPROCESSING") ? "col-8" : "d-none"}>
                                            <p>Analyzing file...</p>
                                            <SpinnerLoader />
                                        </div>
                                        <div className={(reconInfoLoadingState === "FILEINFOLOADED" || reconInfoLoadingState === "EXISTINGFILEINFOLOADED" ) ? "col-8" : "d-none"}>
                                            <div>
                                                <label htmlFor="reconInstrumentNumber" className="required">Instrument Number:</label>
                                                <input autoComplete="off" name="reconInstrumentNumber" type="text" id="reconInstrumentNumber" className="form-control" {...register("reconInstrumentNumber", { required: true })} disabled={orderComplete} />
                                                {errors?.reconInstrumentNumber?.type === "required" && <small className="text-danger d-block">Instrument number is required</small>}
                                            </div>
                                            <div>
                                                <label htmlFor="reconRecordedDate" className="required">Recorded Date:</label>
                                                <input autoComplete="off" name="reconRecordedDate" type="date" id="reconRecordedDate" className="form-control" min="2000-01-01" max="3000-01-01" {...register("reconRecordedDate", { required: true })} disabled={orderComplete} />
                                                {errors?.reconRecordedDate?.type === "required" && <small className="text-danger d-block">Recorded date is required</small>}
                                            </div>
                                            <input type="hidden" {...register("reconFilename", { value: "" })} />
                                            <input type="hidden" {...register("tempId")} />
                                        </div>
                                        <div className="col-4">
                                            <div id="dtDropzone" className="dropzone dropzone-previews border-primary bg-light" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </OutlinedDiv>
                    </div>
                    {renderCurrentDocumentLink("UCC-Unrecorded", `Unrecorded UCC Termination File`)}
                    {renderCurrentDocumentLink("UCC-Recorded", `Current Recorded UCC Termination File`)}
                    {uploadedUccFile && (
                        <div className="mb-3">
                            <OutlinedDiv label="UCC Termination">
                                <div className="row pt-3">
                                    <div className="col-12 col-md-9">
                                        <div className="row">
                                            <div className={(uccInfoLoadingState === "FILEPROCESSING") ? "col-8" : "d-none"}>
                                                <p>Analyzing file...</p>
                                                <SpinnerLoader />
                                            </div>
                                            <div className={(uccInfoLoadingState === "FILEINFOLOADED" || uccInfoLoadingState === "EXISTINGFILEINFOLOADED" ) ? "col-8" : "d-none"}>
                                                <div>
                                                    <label htmlFor="uccInstrumentNumber" className="required">Instrument Number:</label>
                                                    <input autoComplete="off" name="uccInstrumentNumber" type="text" id="uccInstrumentNumber" className="form-control" {...register("uccInstrumentNumber", { required: true })} disabled={orderComplete} />
                                                    {errors?.uccInstrumentNumber?.type === "required" && <small className="text-danger d-block">Instrument number is required</small>}
                                                </div>
                                                <div>
                                                    <label htmlFor="uccRecordedDate" className="required">Recorded Date:</label>
                                                    <input autoComplete="off" name="uccRecordedDate" type="date" id="uccRecordedDate" className="form-control" min="2000-01-01" max="3000-01-01" {...register("uccRecordedDate", { required: true })} disabled={orderComplete} />
                                                    {errors?.uccRecordedDate?.type === "required" && <small className="text-danger d-block">Recorded date is required</small>}
                                                </div>
                                                <div>
                                                    <label htmlFor="uccRecordingFee" className="required">{orderComplete ? "Total Recording Fees:" : "Recording Fee:"}</label>
                                                    <input autoComplete="off" name="uccRecordingFee" type="text" id="uccRecordingFee" className="form-control" {...register("uccRecordingFee", { required: true })} disabled={orderComplete} />
                                                    {errors?.uccRecordingFee?.type === "required" && <small className="text-danger d-block">Recording fee is required</small>}
                                                </div>
                                                <input type="hidden" {...register("uccFilename", { value: "" })} />
                                            </div>
                                            <div className="col-4">
                                                <div id="uccDropzone" className="dropzone dropzone-previews border-primary bg-light" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </OutlinedDiv>
                        </div>  
                    )}
                   
                    <div className="d-flex align-items-center gap-3">
                        {!orderComplete &&
                            <button type="submit" className="btn btn-primary" disabled={submitting || reconInfoLoadingState === "WAITINGFORFILE" || reconInfoLoadingState === "FILEPROCESSING" || (uploadedUccFile && (uccInfoLoadingState === "WAITINGFORFILE" || uccInfoLoadingState === "FILEPROCESSING"))} >Complete Order</button>
                        }
                        <button type="button" className="btn btn-primary" onClick={() => { setReconveyanceOrderIdToEdit(null); setReconveyanceOrderToEdit(null); setSubmitted(false); setError(null); reconDropzone?.removeAllFiles(); uccDropzone?.removeAllFiles(); setOrderComplete(null); setUploadedUccFile(false); setReconInfoLoadingState(null); setUccInfoLoadingState(null); }}> Close</button>
                        {submitting && (
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Submitting...</span>
                            </div>
                        )}
                    </div>
                </div>
            </form>
            <div>
                {!reconveyanceOrderIdToEdit && submitted && <><div className="alert alert-success alert-dismissible" role="alert">Order updated sucessfully!<button type="button" className="btn-close" onClick={() => setSubmitted(false)} aria-label="Close"></button></div></>}
                {!reconveyanceOrderIdToEdit && error && <div className="alert alert-danger" role="alert">{error}</div>}

                <InvoicePaymentModal show={showInvoiceModal} setModalReconveyanceOrderId={setModalReconveyanceOrderId} modalReconveyanceOrderId={modalReconveyanceOrderId} setShowInvoiceModal={setShowInvoiceModal} modalReconveyanceOrder={modalReconveyanceOrder} setSubmitted={setSubmitted} />
                <ReconveyanceCancelModal show={showCancelModal} reconveyanceOrderId={cancelReconveyanceOrderId} setShowCancelModal={setShowCancelModal} setCancelReconveyanceOrderId={setCancelReconveyanceOrderId} setSubmitted={setSubmitted} />

                {<DataGridPro
                    style={reconveyanceOrderIdToEdit && { visibility: 'hidden', position: 'absolute', height: '0'}}
                    rows={ordersToShow || []}
                    columns={columns}
                    getRowId={(row) => row.reconveyanceOrderId}
                    loading={loadingOrders || submitting}
                    slotProps={{
                        loadingOverlay: {
                            variant: 'skeleton',
                            noRowsVariant: 'skeleton',
                        },
                    }}
                    autoHeight
                    getRowHeight={() => 'auto'}
                    disableRowSelectionOnClick
                    alignItems="center"
                    slots={{
                        toolbar: EditToolbar,
                    }}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 25, page: 0 },
                        },
                    }}
                    pagination
                    showToolbar />}
            </div>
        </div>

    );
}

export default ReconveyanceOrders;
